import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

/**
 * Per-user guided-tips preference (UX-GUIDED-TIPS-001).
 *
 * Persisted in localStorage under a per-user key: it is a display preference
 * only, so no backend write API is involved. Consequently it is per browser,
 * not per account across devices. Default is enabled, so first-use guidance
 * appears; every tip is dismissible and the whole feature can be turned off
 * and back on. Storage failures degrade to in-memory state, never to an error.
 */

const STORAGE_PREFIX = "prodigy.guidedTips.v1:";
const DEFAULT_STATE = { enabled: true, dismissed: {} };

function storageKey(userId) {
  return `${STORAGE_PREFIX}${userId}`;
}

export function loadTipsState(userId) {
  if (!userId) return DEFAULT_STATE;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    return {
      enabled: parsed.enabled !== false,
      dismissed:
        parsed.dismissed && typeof parsed.dismissed === "object" && !Array.isArray(parsed.dismissed)
          ? parsed.dismissed
          : {},
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function saveTipsState(userId, state) {
  if (!userId) return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(state));
  } catch {
    // Storage unavailable/full: keep the in-memory state only.
  }
}

const GuidedTipsContext = createContext(null);

export function GuidedTipsProvider({ children }) {
  const { user } = useAuth() || {};
  const userId = user?.id ?? null;
  const [state, setState] = useState(() => loadTipsState(userId));

  // Load the signed-in user's own preference; never inherit a previous user's.
  useEffect(() => {
    setState(loadTipsState(userId));
  }, [userId]);

  const update = useCallback(
    (fn) => {
      setState((prev) => {
        const next = fn(prev);
        saveTipsState(userId, next);
        return next;
      });
    },
    [userId]
  );

  const value = useMemo(
    () => ({
      enabled: state.enabled,
      isDismissed: (route) => Boolean(state.dismissed[route]),
      dismiss: (route) => update((s) => ({ ...s, dismissed: { ...s.dismissed, [route]: true } })),
      setEnabled: (enabled) => update((s) => ({ ...s, enabled: Boolean(enabled) })),
      reset: () => update(() => ({ enabled: true, dismissed: {} })),
    }),
    [state, update]
  );

  return <GuidedTipsContext.Provider value={value}>{children}</GuidedTipsContext.Provider>;
}

export const useGuidedTips = () => useContext(GuidedTipsContext);
